import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService } from '../_services';
import { ToastrService } from 'ngx-toastr';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';

let lastShownMessage = '';
let lastShownTime = 0;

function parseErrorMessage(rawError: any, status?: number): string {
  let errorMsg = 'An error occurred while processing your request.';
  if (!rawError) return errorMsg;

  if (typeof rawError === 'string') {
    const trimmed = rawError.trim();
    if (trimmed.startsWith('<') || trimmed.includes('<!DOCTYPE') || trimmed.includes('<html')) {
      const preMatch = trimmed.match(/<pre>(.*?)<\/pre>/is);
      if (preMatch && preMatch[1]) {
        errorMsg = preMatch[1].replace(/<[^>]*>/g, '').trim();
      } else {
        const stripped = trimmed.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
        if (stripped && stripped.length < 150) {
          errorMsg = stripped;
        } else {
          errorMsg = status ? `HTTP Error ${status}` : 'Server Error';
        }
      }
    } else {
      try {
        const parsed = JSON.parse(trimmed);
        errorMsg = parsed.message || parsed.error || trimmed;
      } catch {
        errorMsg = trimmed;
      }
    }
  } else if (typeof rawError === 'object') {
    errorMsg = rawError.message || rawError.error || rawError.detail || errorMsg;
  }

  return errorMsg;
}

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {
  const authenticationService = inject(AuthenticationService);
  const toastr = inject(ToastrService);

  const currentUser = authenticationService ? authenticationService.currentUserValue : null;
  const rawUser = localStorage.getItem('currentUser');
  const userdata = rawUser ? JSON.parse(rawUser) : null;
  const isLoggedIn = !!(currentUser?.token || userdata?.token);
  const isAuthEndpoint = req.url.includes('/auth-login') || req.url.includes('/login') || req.url.includes('/auto-login');

  if (isLoggedIn) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${currentUser?.token || userdata?.token}`,
      },
    });
  }

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const body: any = event.body;
        if (body && (body.status === false || body.status === 'false' || body.code == 429 || body.status === 'error' || (typeof body.message === 'string' && body.message.toLowerCase().includes('quota')))) {
          const rawMsg = body.message || body.error || (body.code == 429 ? 'Quota Exceeded: You have reached your search limit.' : 'An error occurred.');
          const errorMsg = parseErrorMessage(rawMsg);

          const lowerMsg = (typeof errorMsg === 'string' ? errorMsg : '').toLowerCase();
          const isInactiveOrSuspended = lowerMsg.includes('suspended') || lowerMsg.includes('inactive') || lowerMsg.includes('access denied');
          const isQuota = body.code == 429 || (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('quota'));

          // Do NOT show toasts for auth endpoints (auto-login/login) or if user is not logged in (unless quota error)
          if (errorMsg && !isAuthEndpoint && (isLoggedIn || isQuota)) {
            const now = Date.now();
            if (lastShownMessage !== errorMsg || (now - lastShownTime) > 1000) {
              lastShownMessage = errorMsg;
              lastShownTime = now;
              toastr.warning(errorMsg, isQuota ? 'Quota Exceeded' : (isInactiveOrSuspended ? 'Account Inactive' : 'Error'));
            }
          }

          if (isInactiveOrSuspended && !isAuthEndpoint && isLoggedIn) {
            localStorage.clear();
            sessionStorage.clear();
            if (window.location.pathname !== '/') {
              setTimeout(() => {
                window.location.href = '/';
              }, 1500);
            }
          }
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      // Do not show toasts for 404 Not Found (missing routes/resources)
      if (error.status === 404) {
        return throwError(() => error);
      }

      let errorMsg = parseErrorMessage(error.error || error.message, error.status);
      let isQuota = error.status === 429 || (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('quota'));
      const lowerMsg = (typeof errorMsg === 'string' ? errorMsg : '').toLowerCase();
      const isInactiveOrSuspended = error.status === 401 || error.status === 403 || lowerMsg.includes('suspended') || lowerMsg.includes('inactive') || lowerMsg.includes('access denied');

      let summary = isQuota ? 'Quota Exceeded' : (isInactiveOrSuspended ? 'Account Inactive' : 'Error');

      // Do NOT show toasts for auth endpoints (auto-login/login) or if user is not logged in (unless quota error)
      if (!isAuthEndpoint && (isLoggedIn || isQuota)) {
        const now = Date.now();
        if (lastShownMessage !== errorMsg || (now - lastShownTime) > 1000) {
          lastShownMessage = errorMsg;
          lastShownTime = now;
          toastr.error(errorMsg, summary);
        }
      }

      if (isInactiveOrSuspended && !isAuthEndpoint && isLoggedIn) {
        localStorage.clear();
        sessionStorage.clear();
        if (window.location.pathname !== '/') {
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        }
      }

      return throwError(() => error);
    })
  );
};

